import { Fragment } from 'react';
import { toBn } from '../lib/utils';

const WindowChrome = ({ color, title, children }) => (
  <div className="overflow-hidden rounded-xl bg-white shadow-2xl shadow-slate-900/20 ring-1 ring-slate-900/10">
    <div className={`flex items-center gap-2 px-3 py-2 text-xs font-medium text-white ${color}`}>
      <span className="flex gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full bg-white/40" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/40" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/40" />
      </span>
      <span className="truncate">{title}</span>
    </div>
    {children}
  </div>
);

const Ribbon = ({ tabs, active, activeClass }) => (
  <div className="flex gap-3 overflow-hidden border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-[10px] text-slate-500">
    {tabs.map((t) => (
      <span key={t} className={t === active ? `border-b-2 font-semibold ${activeClass}` : ''}>
        {t}
      </span>
    ))}
  </div>
);

const salaryRows = [
  ['রহিম', 25000, 3000],
  ['করিম', 32000, 4500],
  ['সুমি', 28000, 3500],
  ['নাদিয়া', 35000, 5000],
];

export function ExcelMockup() {
  const total = salaryRows.reduce((sum, [, basic, bonus]) => sum + basic + bonus, 0);
  const max = Math.max(...salaryRows.map(([, b, x]) => b + x));

  return (
    <WindowChrome color="bg-excel-500" title="Salary_Sheet.xlsx — Excel">
      <Ribbon tabs={['File', 'Home', 'Insert', 'Formulas', 'Data', 'View']} active="Formulas" activeClass="border-excel-500 text-excel-600" />
      <div className="flex items-center gap-2 border-b border-slate-200 px-2 py-1 font-mono text-[10px]">
        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-500">D6</span>
        <span className="text-slate-400">fx</span>
        <span className="text-excel-600">=SUM(D2:D5)</span>
      </div>
      <div className="grid grid-cols-[1.5rem_1fr_1fr_1fr_1fr] text-[10px] sm:text-[11px]">
        {['', 'A', 'B', 'C', 'D'].map((c) => (
          <div key={c} className="border-b border-r border-slate-200 bg-slate-50 py-0.5 text-center text-slate-400">
            {c}
          </div>
        ))}
        {[['নাম', 'বেসিক', 'বোনাস', 'মোট'], ...salaryRows.map(([n, b, x]) => [n, b, x, b + x])].map((row, r) => (
          <Row key={r} n={r + 1} cells={row} header={r === 0} />
        ))}
        <Row n={6} cells={['', '', 'Total', total]} total />
      </div>
      <div className="flex h-16 items-end gap-2 border-t border-slate-100 px-4 pb-2 pt-3">
        {salaryRows.map(([n, b, x]) => (
          <div key={n} className="flex flex-1 flex-col items-center gap-1">
            <div className="w-full rounded-t bg-excel-500/80" style={{ height: `${((b + x) / max) * 40}px` }} />
            <span className="text-[9px] text-slate-400">{n}</span>
          </div>
        ))}
      </div>
    </WindowChrome>
  );
}

function Row({ n, cells, header, total }) {
  const fmt = (v) => (typeof v === 'number' ? toBn(v.toLocaleString('en-IN')) : v);
  return (
    <>
      <div className="border-b border-r border-slate-200 bg-slate-50 py-1 text-center text-slate-400">{n}</div>
      {cells.map((c, i) => (
        <div
          key={i}
          className={`truncate border-b border-r border-slate-200 px-1.5 py-1 ${typeof c === 'number' ? 'text-right' : ''} ${
            header ? 'bg-excel-50 font-semibold text-excel-700' : ''
          } ${total && i === 3 ? 'bg-excel-100 font-bold text-excel-700 ring-2 ring-inset ring-excel-500' : ''} ${
            total && i === 2 ? 'font-semibold' : ''
          }`}
        >
          {fmt(c)}
        </div>
      ))}
    </>
  );
}

const results = [
  ['রাফি', 92],
  ['তানিয়া', 78],
  ['সাকিব', 64],
  ['মিম', 85],
];
// Same rule as the formula shown in the mockup's formula bar.
const grade = (marks) => (marks >= 80 ? 'A+' : marks >= 70 ? 'A' : 'A-');
const gradeStyle = { 'A+': 'bg-excel-100 text-excel-700', A: 'bg-amber-50 text-amber-700', 'A-': 'bg-red-50 text-red-600' };

export function ResultMockup() {
  const cell = 'truncate border-b border-r border-slate-200 px-1.5 py-1';
  const head = 'border-b border-r border-slate-200 bg-slate-50 py-0.5 text-center text-slate-400';
  return (
    <WindowChrome color="bg-excel-600" title="Result_Sheet.xlsx — Excel">
      <Ribbon tabs={['File', 'Home', 'Insert', 'Formulas', 'Data', 'View']} active="Home" activeClass="border-excel-500 text-excel-600" />
      <div className="flex items-center gap-2 border-b border-slate-200 px-2 py-1 font-mono text-[10px]">
        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-500">C2</span>
        <span className="text-slate-400">fx</span>
        <span className="truncate text-excel-600">=IF(B2&gt;=80,"A+",IF(B2&gt;=70,"A","A-"))</span>
      </div>
      <div className="grid grid-cols-[1.5rem_1.4fr_1fr_1fr] text-[10px] sm:text-[11px]">
        {['', 'A', 'B', 'C'].map((c) => (
          <div key={c} className={head}>
            {c}
          </div>
        ))}
        <div className={head}>1</div>
        {['নাম', 'নম্বর', 'গ্রেড'].map((h) => (
          <div key={h} className={`${cell} bg-excel-50 font-semibold text-excel-700`}>
            {h}
          </div>
        ))}
        {results.map(([name, marks], i) => (
          <Fragment key={name}>
            <div className={head}>{i + 2}</div>
            <div className={cell}>{name}</div>
            <div className={`${cell} text-right`}>{toBn(marks)}</div>
            <div className={`${cell} text-center font-bold ${gradeStyle[grade(marks)]} ${i === 0 ? 'ring-2 ring-inset ring-excel-500' : ''}`}>{grade(marks)}</div>
          </Fragment>
        ))}
      </div>
    </WindowChrome>
  );
}
